import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { SortUtils } from '../../utils/sort-utils';
import { SnackBarService } from 'src/app/core/services/snackbar.service';
import { ServerErrorUtils } from '../../utils/server-error-utils';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { Deals } from '../../domain/pmp-deals.model';
import { ActiveInactiveArchived } from '../../enums/active-inactive-archived.enum';
import { PmpDealsService } from '../../services/pmp-deals.service';
import { ActiveInactiveArchivedColorMap } from 'src/app/shared/enums/active-inactive-archived.enum';

@Component({
  selector: 'ctv-deals-autocomplete',
  template: ` <mat-form-field
    [floatLabel]="floatLabel || 'always'"
    class="w-100"
    [ngClass]="formFieldClass"
    [appearance]="formFieldAppearance"
  >
    <mat-label>{{label ? label : 'Select deal'}}</mat-label>
    <input
      matInput
      #search
      aria-label="Deals"
      [matAutocomplete]="auto"
      [formControl]="filterCtrl"
      [placeholder]="placeholder ? placeholder : 'Search'"
      [required]="required"
    />
    <i class="far fa-spin fa-spinner text-primary" matSuffix [hidden]="!loading"></i>
    <mat-autocomplete
      #auto="matAutocomplete"
      (optionSelected)="optionSelected($event)"
      [displayWith]="display"
    >
      <mat-option *ngFor="let item of filteredItems | async" [value]="item">
        <i class="fas fa-circle status-circle small" ngClass="text-{{DealsStatusColorMap[item.status]}}"></i>
        {{ item.name }}
      </mat-option>
    </mat-autocomplete>
  </mat-form-field>`,
})
export class DealsAutocompleteComponent implements OnInit, OnDestroy {
  @ViewChild('search', { static: true }) searchFilter: ElementRef;
  private readonly onDestroy = new Subject<void>();

  @Input() public placeholder: string;
  @Input() public floatLabel: string;
  @Input() public label: string;
  @Input() public formFieldClass: string;
  @Input() public formFieldAppearance: string;
  @Input() required: boolean;
  @Input() preselectedDealId: Deals['id'];
  @Output() dealsSelected = new EventEmitter<Deals>();

  filteredItems: Subject<Deals[]>; //List of Pubs to show in dropdown filtered with 'filteredDealIds'
  filterCtrl = new UntypedFormControl();
  dealsList: Deals[] = []; //stores currently fetched list of deals
  selectedDeal: Deals; //currently selected deal
  filteredDealIds: string[] = []; //It stores all the Id's of selected deals
  public loading = false;
  DealsStatusColorMap = ActiveInactiveArchivedColorMap;

  constructor(
    private service: PmpDealsService,
    private snackBarService: SnackBarService) {
    this.filteredItems = new Subject();
    this.filterCtrl.valueChanges
      .pipe(debounceTime(800), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(
        (searchTerm) => {
          if (searchTerm && typeof searchTerm === 'string') {
            this.getDeals(searchTerm);
          }
          else if (searchTerm == '') { //triggers when user deletes everything from input
            this.filteredItems.next();
            this.dealsSelected.emit(null);
          }
        }
      );
  }

  ngOnInit(): void {
    if (this.preselectedDealId) {
      this.getDeals('', this.preselectedDealId);
    }
  }

  /** SearchTerm used for filtering by deal name. PreselectedDealId used to get only one deal (preselected) at the component initialization. */
  private getDeals(searchTerm?: string, preselectedDealId?: string) {
    this.loading = true;
    this.filterCtrl.disable();
    return this.service.getDealsList({
      page: 0,
      size: 1000,
      name: searchTerm,
      ids: [preselectedDealId]
    })
      .pipe(
        finalize(() => {
          this.filterCtrl.enable();
          !preselectedDealId ? this.searchFilter.nativeElement.focus() : 0; //No need for focus when loading page with preselected deal
          this.loading = false;
        }),
        takeUntil(this.onDestroy)
      )
      .subscribe(
        (resp) => {
          this.dealsList = resp.content.sort(SortUtils.propertyComparatorString('name'));
          if (preselectedDealId) {
            this.selectedDeal = this.dealsList[0]; //Naturally since the api call is filtered with one ID, selectedDeal will actually be the first one from the response
            this.filterCtrl.setValue(this.selectedDeal);
            this.updateFilteredDeals([this.selectedDeal]);
            this.emit();
          }
          this.filteredItems.next(this.dealsList.filter(x => !this.filteredDealIds.includes(x.id)));
        },
        (error) => {
          const messages = ServerErrorUtils.getValidationMessages(error);
          if (messages) {
            messages.forEach((m) => this.snackBarService.error(m));
          } else {
            this.snackBarService.error('Error while fetching deals autocomplete data');
          }
        },
      );
  }

  public optionSelected($event) {
    this.selectedDeal = $event.option.value;
    this.emit();
    this.searchFilter.nativeElement.blur(); //blur == unfocus
    this.filteredItems.next(); //Reset list since the input value of autocomplete is empty and there is no point in showing filtered list on next focus on element
  }

  /** Emits value back to parent component.
   * If wrapped inside chips autocomplete it emits value to chips component that forwards values to parent component. */
  private emit() {
    this.dealsSelected.emit(this.selectedDeal);
  }

  /** Trigger only when using chips autocomplete. Triggered when item selected through chips autocomplete to update filteredDealsIds. */
  public updateFilteredDeals(items) {
    this.filteredDealIds = items.map((item) => item.id);
  }

  /** Returns wanted object to input from whole selected item model. */
  public display(p?: Deals) {
    return p ? p.name : undefined;
  }

  /** Used only by chips autocomplete component to clear the input from selected item*/
  public resetInput() {
    this.filterCtrl.setValue(null);
    this.filteredItems.next(); //Reset list since the input value of autocomplete is empty
  }

  public disable() {
    this.filterCtrl.disable();
  }

  public enable() {
    this.filterCtrl.enable();
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
  }
}
